'use client';

import * as React from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel, FormError, Input } from '@/components/ui/field';
import { useHydrated } from '@/lib/hooks/use-hydrated';
import { loginSchema, type LoginInput } from '@/lib/validation/schemas';
import { loginAction } from '@/server/actions';

export function LoginForm() {
  const router = useRouter();
  const [formError, setFormError] = React.useState<string | null>(null);
  const [showPassword, setShowPassword] = React.useState(false);
  // Until this is true, pressing Sign in would submit the form natively — a GET
  // carrying the password in the query string. See `useHydrated`.
  const hydrated = useHydrated();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { identifier: '', password: '' },
  });

  async function onSubmit(values: LoginInput) {
    setFormError(null);
    const result = await loginAction(values);

    if (!result.ok) {
      setFormError(result.message);
      return;
    }

    // A full navigation rather than a client push, so the new session cookie is
    // picked up by the server components on the next render.
    router.push(result.data.redirectTo);
    router.refresh();
  }

  return (
    // `method="post"` is belt and braces: the button below cannot be pressed
    // before hydration, but if a submit were ever provoked another way, the
    // credentials would travel in a body rather than in the URL.
    <form onSubmit={handleSubmit(onSubmit)} method="post" className="mt-5 space-y-4" noValidate>
      <FormError message={formError} />

      <Field error={errors.identifier?.message}>
        <FieldLabel required>Email, username, or mobile</FieldLabel>
        <Input
          {...register('identifier')}
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          placeholder="owner or 01712345678"
          enterKeyHint="next"
        />
      </Field>

      <Field error={errors.password?.message}>
        <FieldLabel required>Password</FieldLabel>
        <div className="relative">
          <Input
            {...register('password')}
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            className="pr-11"
            enterKeyHint="go"
          />
          <button
            type="button"
            onClick={() => setShowPassword((current) => !current)}
            className="absolute right-1 top-1/2 flex size-9 -translate-y-1/2 items-center justify-center rounded-[var(--radius-control)] text-ink-subtle hover:bg-surface-muted"
            aria-pressed={showPassword}
          >
            {showPassword ? <EyeOff className="size-4" aria-hidden="true" /> : <Eye className="size-4" aria-hidden="true" />}
            <span className="sr-only">{showPassword ? 'Hide password' : 'Show password'}</span>
          </button>
        </div>
      </Field>

      <Button type="submit" block size="lg" disabled={!hydrated} loading={isSubmitting} loadingLabel="Signing in…">
        Sign in
      </Button>
    </form>
  );
}
